import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import type { AdminCountry } from '@/features/auth';
import { useCatalogueHealth } from '@/features/pricing';
import { StatusBadge } from '@/shared/components/status-badge';
import { reasonLabel } from '@/shared/lib/catalogue-labels';
import { formatCurrency, formatNumber } from '@/shared/lib/format';

import { GROUPING_LABEL } from '../lib/report-labels';
import type {
  FinancialReport,
  OperationalReport,
  PricingReport,
  ProviderReport,
} from '../types/report.types';

const pct = (value: number | null) => (value === null ? '—' : `${value}%`);
const num = 'text-end tabular-nums';

/** Pricing report: margin against target, price health and guardrails; savings with cost access. */
export function PricingTables({ report }: { report: PricingReport }) {
  const showProfit = report.rows.some((row) => row.grossProfit !== null);
  return (
    <div className="space-y-4">
      <Card>
        <CardHeader>
          <CardTitle className="text-sm">
            Pricing by {GROUPING_LABEL[report.groupBy].toLowerCase()}
          </CardTitle>
        </CardHeader>
        <CardContent className="overflow-x-auto">
          <Table aria-label="Pricing">
            <TableHeader>
              <TableRow>
                <TableHead>{GROUPING_LABEL[report.groupBy]}</TableHead>
                <TableHead>Currency</TableHead>
                <TableHead className="text-end">Priced orders</TableHead>
                <TableHead className="text-end">Avg margin</TableHead>
                <TableHead className="text-end">Avg target</TableHead>
                <TableHead className="text-end">Below target</TableHead>
                <TableHead className="text-end">At/near floor</TableHead>
                <TableHead className="text-end">At ceiling</TableHead>
                <TableHead className="text-end">Guardrail applied</TableHead>
                {showProfit && <TableHead className="text-end">Gross profit</TableHead>}
              </TableRow>
            </TableHeader>
            <TableBody>
              {report.rows.map((row) => (
                <TableRow key={`${row.key}-${row.currencyCode}`}>
                  <TableCell>{row.label}</TableCell>
                  <TableCell>{row.currencyCode ?? '—'}</TableCell>
                  <TableCell className={num}>{formatNumber(row.pricedOrders)}</TableCell>
                  <TableCell className={num}>{pct(row.avgRealizedMarginPct)}</TableCell>
                  <TableCell className={num}>{pct(row.avgTargetMarginPct)}</TableCell>
                  <TableCell className={num}>
                    {row.belowTargetOrders > 0 ? (
                      <StatusBadge variant="warning">
                        {formatNumber(row.belowTargetOrders)}
                      </StatusBadge>
                    ) : (
                      0
                    )}
                  </TableCell>
                  <TableCell className={num}>{formatNumber(row.atNearFloorOrders)}</TableCell>
                  <TableCell className={num}>{formatNumber(row.atCeilingOrders)}</TableCell>
                  <TableCell className={num}>{formatNumber(row.guardrailOrders)}</TableCell>
                  {showProfit && (
                    <TableCell className={num}>
                      {row.grossProfit === null
                        ? '—'
                        : formatCurrency(row.grossProfit, row.currencyCode ?? 'USD')}
                    </TableCell>
                  )}
                </TableRow>
              ))}
            </TableBody>
          </Table>
          {report.unpricedOrders > 0 && (
            <p className="text-muted-foreground mt-2 text-xs">
              {formatNumber(report.unpricedOrders)} completed order(s) were priced before dynamic
              pricing and have no pricing snapshot; they are not included.
            </p>
          )}
        </CardContent>
      </Card>

      {report.savings && (
        <Card>
          <CardHeader>
            <CardTitle className="text-sm">Procurement savings</CardTitle>
          </CardHeader>
          <CardContent>
            {report.savings.length === 0 ? (
              <p className="text-muted-foreground text-xs">
                No order had a competing provider in this period.
              </p>
            ) : (
              <Table aria-label="Procurement savings">
                <TableHeader>
                  <TableRow>
                    <TableHead>Provider cost currency</TableHead>
                    <TableHead className="text-end">Orders with a choice</TableHead>
                    <TableHead className="text-end">Saved against the next provider</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {report.savings.map((saving) => (
                    <TableRow key={saving.currencyCode}>
                      <TableCell>{saving.currencyCode}</TableCell>
                      <TableCell className={num}>
                        {formatNumber(saving.ordersWithCompetition)}
                      </TableCell>
                      <TableCell className={num}>
                        {formatCurrency(saving.totalSaving, saving.currencyCode)}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </CardContent>
        </Card>
      )}
    </div>
  );
}

/** Provider report: volume, success, share and wins; current balance with providers.read. */
export function ProviderTables({ report }: { report: ProviderReport }) {
  const showBalance = report.rows.some((row) => row.currentBalance !== null);
  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-sm">Providers</CardTitle>
      </CardHeader>
      <CardContent className="overflow-x-auto">
        <Table aria-label="Providers">
          <TableHeader>
            <TableRow>
              <TableHead>Provider</TableHead>
              <TableHead className="text-end">Orders</TableHead>
              <TableHead className="text-end">Completed</TableHead>
              <TableHead className="text-end">Failed</TableHead>
              <TableHead className="text-end">Success rate</TableHead>
              <TableHead className="text-end">Share of completed</TableHead>
              <TableHead className="text-end">Won against a competitor</TableHead>
              {showBalance && <TableHead className="text-end">Balance now</TableHead>}
            </TableRow>
          </TableHeader>
          <TableBody>
            {report.rows.map((row) => (
              <TableRow key={row.providerId ?? 'none'}>
                <TableCell>{row.providerName}</TableCell>
                <TableCell className={num}>{formatNumber(row.orders)}</TableCell>
                <TableCell className={num}>{formatNumber(row.completed)}</TableCell>
                <TableCell className={num}>{formatNumber(row.failed)}</TableCell>
                <TableCell className={num}>{pct(row.successRatePct)}</TableCell>
                <TableCell className={num}>{pct(row.shareOfCompletedPct)}</TableCell>
                <TableCell className={num}>{formatNumber(row.wonAgainstCompetitor)}</TableCell>
                {showBalance && (
                  <TableCell className={num}>
                    {row.currentBalance === null ? (
                      '—'
                    ) : (
                      <span className="inline-flex items-center gap-1.5">
                        {formatCurrency(row.currentBalance, row.currencyCode ?? 'USD')}
                        {row.isLowBalance && <StatusBadge variant="warning">Low</StatusBadge>}
                      </span>
                    )}
                  </TableCell>
                )}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </CardContent>
    </Card>
  );
}

/** Catalogue: what each of my countries offers, shows and can sell right now (catalogue.read). */
export function CatalogueByCountry({ countries }: { countries: readonly AdminCountry[] }) {
  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-sm">Catalogue by country (now)</CardTitle>
      </CardHeader>
      <CardContent>
        <Table aria-label="Catalogue by country">
          <TableHeader>
            <TableRow>
              <TableHead>Country</TableHead>
              <TableHead className="text-end">Offered</TableHead>
              <TableHead className="text-end">Visible</TableHead>
              <TableHead className="text-end">Sellable</TableHead>
              <TableHead>Main reason not sellable</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {countries.map((country) => (
              <CatalogueRow key={country.id} country={country} />
            ))}
          </TableBody>
        </Table>
      </CardContent>
    </Card>
  );
}

function CatalogueRow({ country }: { country: AdminCountry }) {
  const health = useCatalogueHealth(country.id);
  const name = country.nameEn ?? country.code ?? `#${country.id}`;
  if (health.isLoading)
    return (
      <TableRow>
        <TableCell>{name}</TableCell>
        <TableCell colSpan={4}>
          <Skeleton className="h-4" />
        </TableCell>
      </TableRow>
    );
  if (health.error || !health.data)
    return (
      <TableRow>
        <TableCell>{name}</TableCell>
        <TableCell colSpan={4} className="text-muted-foreground">
          Could not load
        </TableCell>
      </TableRow>
    );
  const top = Object.entries(health.data.notSellableByReason).sort((a, b) => b[1] - a[1])[0];
  return (
    <TableRow>
      <TableCell>{name}</TableCell>
      <TableCell className={num}>{formatNumber(health.data.offeredCount)}</TableCell>
      <TableCell className={num}>{formatNumber(health.data.visibleCount)}</TableCell>
      <TableCell className={num}>{formatNumber(health.data.sellableCount)}</TableCell>
      <TableCell className="text-muted-foreground">
        {top ? `${reasonLabel(top[0])} (${formatNumber(top[1])})` : '—'}
      </TableCell>
    </TableRow>
  );
}

/**
 * Country comparison: the operational and (with reports.financial.read)
 * financial reports grouped by country, side by side. Money stays per currency.
 */
export function CountryComparison({
  operational,
  financial,
}: {
  operational: OperationalReport;
  financial: FinancialReport | null;
}) {
  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-sm">Countries compared</CardTitle>
      </CardHeader>
      <CardContent className="overflow-x-auto">
        <Table aria-label="Countries compared">
          <TableHeader>
            <TableRow>
              <TableHead>Country</TableHead>
              <TableHead className="text-end">Orders</TableHead>
              <TableHead className="text-end">Completed</TableHead>
              <TableHead className="text-end">Success rate</TableHead>
              {financial && <TableHead className="text-end">Revenue</TableHead>}
              {financial && <TableHead className="text-end">Margin</TableHead>}
            </TableRow>
          </TableHeader>
          <TableBody>
            {operational.rows.map((row) => {
              const money = financial?.rows.filter((f) => f.key === row.key) ?? [];
              return (
                <TableRow key={row.key}>
                  <TableCell>{row.label}</TableCell>
                  <TableCell className={num}>{formatNumber(row.orders)}</TableCell>
                  <TableCell className={num}>{formatNumber(row.completed)}</TableCell>
                  <TableCell className={num}>{pct(row.successRatePct)}</TableCell>
                  {financial && (
                    <TableCell className={num}>
                      {money.length === 0
                        ? '—'
                        : money
                            .map((m) => formatCurrency(m.revenue, m.currencyCode ?? 'USD'))
                            .join(' · ')}
                    </TableCell>
                  )}
                  {financial && (
                    <TableCell className={num}>
                      {money.length === 0
                        ? '—'
                        : money.map((m) => pct(m.realizedMarginPct)).join(' · ')}
                    </TableCell>
                  )}
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </CardContent>
    </Card>
  );
}
